/**
 * Иерархия ошибок API-клиента.
 *
 * Фактический формат ошибок API (`apps/api/src/plugins/errorHandler.ts:43`) —
 * `{ error: '<строка-код>' }`, например `{ error: 'moderation_unavailable' }`.
 * Это НЕ объект `{ code, message }`.
 *
 * Для **401 сервер добавляет вторым плоским полем** `code` с причиной отказа
 * initData: `{ error: 'unauthorized', code: 'stale' | 'hmac' | … }`
 * (`apps/api/src/plugins/auth.ts:41,54`). Строка из `error` едет в `ApiError.code`
 * (на нём висит разбор `sender_banned` в `lib/apiError.ts`), а плоский `code`
 * из 401 — в отдельное поле `ApiAuthError.reason`.
 *
 * Клиент маппит:
 * - 401 → `ApiAuthError` (CANON SP3: UI должен показать «перезапустите Mini App»)
 * - 429 → `ApiRateLimitError`
 * - все остальные 4xx/5xx → `ApiError`
 */

/**
 * Причина отказа initData с сервера — тело 401: `{ error:'unauthorized', code:'stale'|… }`.
 * Источник истины: `apps/api/src/lib/initdata.ts:37` (`InitDataErrorCode`).
 *
 * Сверх серверного списка два клиентских значения:
 * - `'no-initdata'` — initData вообще нет, запрос не делался (ранний выброс в `client.ts`);
 * - `'unknown'` — тело 401 без `code` либо с незнакомым значением (CANON G3: не гадаем).
 */
export type AuthFailureReason =
  | 'stale'
  | 'hmac'
  | 'missing'
  | 'malformed'
  | 'user-parse'
  | 'no-initdata'
  | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class ApiAuthError extends ApiError {
  /**
   * Причина отказа initData. Аддитивно к `code`: `code` остаётся строкой из поля
   * `error` (`'unauthorized'`), `reason` несёт плоское поле `code` тела 401.
   * Дефолт `'unknown'` — существующие вызовы с двумя аргументами не ломаются.
   */
  readonly reason: AuthFailureReason;

  constructor(message: string, code?: string, reason: AuthFailureReason = 'unknown') {
    super(401, message, code);
    this.name = 'ApiAuthError';
    this.reason = reason;
  }
}

export class ApiRateLimitError extends ApiError {
  constructor(message: string, code?: string) {
    super(429, message, code);
    this.name = 'ApiRateLimitError';
  }
}

/**
 * Ошибка парсинга схемы: сервер ответил валидным 2xx-ответом, но форма
 * тела не совпала с ожидаемой zod-схемой. Это НЕ сетевая ошибка.
 *
 * Причины: устаревший клиент (клиент ожидает другой контракт) или баг сервера.
 * `humanizeApiError` распознаёт этот тип и показывает `err_schema` вместо
 * `err_network` — не вводя пользователя в заблуждение «нет связи».
 */
export class ApiParseError extends Error {
  override readonly cause: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'ApiParseError';
    this.cause = cause;
  }
}
